/**
 * GroupsSection - Groups management and display
 */
import { Button } from "@/components/ui/button";
import { Users, Plus, UserPlus, Crown, Copy, Check, ChevronRight, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const GroupsSection = ({
  user,
  groups,
  onCreateGroup,
  onJoinGroup,
  onLeaveGroup,
  onTransferOwnership,
  onOpenGroup,
}) => {
  const [copiedGroupCode, setCopiedGroupCode] = useState(null);

  const copyGroupCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedGroupCode(code);
      toast.success("Group invite code copied!");
      setTimeout(() => setCopiedGroupCode(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // Social proof: count unique non-self members across all groups + build a small avatar stack
  const allMembers = groups.flatMap((g) => g.members || []);
  const uniqueOthers = Array.from(
    new Map(
      allMembers
        .filter((m) => m.id && m.id !== user?.id)
        .map((m) => [m.id, m]),
    ).values(),
  );
  const avatarStack = uniqueOthers.slice(0, 5);
  const initialOf = (name) => (name || "?").trim().slice(0, 1).toUpperCase();

  return (
    <section className="bg-card rounded-2xl border border-charcoal/5 shadow-card overflow-hidden animate-fade-in">
      <div className="p-6 border-b border-charcoal/5">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-secondary" />
          <h2 className="font-serif text-xl font-medium text-charcoal">Groups</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Create or join groups to share lists with friends
        </p>
      </div>

      <div className="p-6 space-y-4">
        {/* Social proof row — visible only when the user has at least one group */}
        {groups.length > 0 && (
          <div
            className="flex items-center gap-3 p-3 rounded-xl bg-secondary/5 border border-secondary/10"
            data-testid="groups-social-proof"
          >
            {avatarStack.length > 0 ? (
              <div className="flex -space-x-2 flex-shrink-0">
                {avatarStack.map((m, idx) => (
                  <div
                    key={m.id || idx}
                    title={m.name || "Member"}
                    className="w-7 h-7 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center text-[11px] font-semibold text-white border-2 border-card shadow-sm"
                  >
                    {initialOf(m.name)}
                  </div>
                ))}
                {uniqueOthers.length > avatarStack.length && (
                  <div className="w-7 h-7 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold flex items-center justify-center border-2 border-card shadow-sm">
                    +{uniqueOthers.length - avatarStack.length}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <Users className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
            )}
            <p className="text-sm text-charcoal flex-1 min-w-0">
              <span className="font-semibold">
                {groups.length} {groups.length === 1 ? "group" : "groups"}
              </span>
              <span className="text-muted-foreground"> · </span>
              <span className="font-semibold">
                {uniqueOthers.length} {uniqueOthers.length === 1 ? "friend" : "friends"}
              </span>
            </p>
          </div>
        )}

        {/* Existing Groups */}
        {groups.length > 0 && (
          <div className="space-y-3">
            {groups.map((group) => {
              const isAdmin = group.admin_id === user?.id;
              return (
                <div
                  key={group.id}
                  className="p-4 bg-secondary/5 rounded-xl border border-secondary/20"
                >
                  {/* Header — tap to open the group's items. Was a no-op
                      before this fix; users could see the group on Profile
                      but had no way to access its bucket list. */}
                  <button
                    type="button"
                    onClick={() => onOpenGroup?.(group)}
                    className="w-full flex items-center gap-3 mb-3 text-left -m-1 p-1 rounded-lg hover:bg-secondary/10 active:bg-secondary/15 transition-colors"
                    data-testid={`profile-open-group-${group.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5 text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-charcoal truncate">{group.name}</p>
                        {isAdmin && (
                          <span className="flex items-center gap-1 px-2 py-0.5 text-xs bg-yellow-500/10 text-yellow-700 rounded-full">
                            <Crown className="w-3 h-3" />
                            Admin
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {group.members?.length || 1} member{(group.members?.length || 1) !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                  
                  {/* Group Invite Code */}
                  <div className="flex items-center gap-2 p-2 bg-background rounded-lg mb-3">
                    <code className="flex-1 text-sm font-mono text-muted-foreground">
                      {group.invite_code}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyGroupCode(group.invite_code)}
                      className="h-7 px-2"
                    >
                      {copiedGroupCode === group.invite_code ? (
                        <Check className="w-3 h-3 text-green-500" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </Button>
                  </div>

                  {/* Group Actions */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => onOpenGroup?.(group)}
                      className="flex-1 h-9 text-xs bg-secondary text-white hover:bg-secondary/90 rounded-lg"
                      data-testid={`profile-open-group-btn-${group.id}`}
                    >
                      Open
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                    {isAdmin && group.members?.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onTransferOwnership(group)}
                        className="flex-1 h-9 text-xs rounded-lg"
                      >
                        <Crown className="w-3 h-3 mr-1" />
                        Transfer
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onLeaveGroup(group.id, group.name)}
                      className="flex-1 h-9 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg border-red-200"
                    >
                      {isAdmin ? "Delete" : "Leave"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={onCreateGroup}
            className="flex-1 h-12 bg-secondary text-white hover:bg-secondary/90 rounded-xl btn-press"
            data-testid="create-group-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Group
          </Button>
          <Button
            variant="outline"
            onClick={onJoinGroup}
            className="flex-1 h-12 rounded-xl border-charcoal/10"
            data-testid="join-group-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Join Group
          </Button>
        </div>
      </div>
    </section>
  );
};

export default GroupsSection;
